import { AppSettings } from "@/AppSettings";
import { ConfigurationStatus, Customer } from "@/api/dtos";
import SettingsDialog from "@/components/dialogs/SettingsDialog";
import AuthenticatedUserContext from "@/contexts/authentication/AuthenticatedUserContext";
import InstitutionContext from "@/contexts/data/InstitutionContext";
import ParticipantContext from "@/contexts/data/ParticipantContext";
import TrialContext from "@/contexts/data/TrialContext";
import ChosenThemeActionContext from "@/contexts/styles/ChosenThemeActionContext";
import ChosenThemeContext from "@/contexts/styles/ChosenThemeContext";
import { faArrowAltToBottom, faArrowAltToTop, faMegaphone, faMoon, faSun, faUserCircle } from '@fortawesome/pro-duotone-svg-icons';
import { faBars, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AppBar, Box, Divider, Drawer, IconButton, Link, List, ListItem, ListItemButton, ListItemIcon, ListItemText, Menu, MenuItem, Toolbar, Tooltip, Typography, darken, lighten, styled, useTheme } from "@mui/material";
import * as Sentry from "@sentry/react";
import { FC, MouseEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, Link as RouteLink, matchPath, useLocation } from "react-router-dom";
import Image from '../Image';
import ShowOnScroll, { bottomTrigger } from "../ShowOnScroll";
import NavigationInstitutionSelector from "./NavigationInstitutionSelector";
import NavigationParticipantSelector from "./NavigationParticipantSelector";
import NavigationTrialSelector from "./NavigationTrialSelector";

import colorBctLogo from '@/assets/bct/img/logo-h-c.png';
import whiteBctLogo from '@/assets/bct/img/logo-h-w.png';


import colorTrogLogo from '@/assets/trog/img/logo-h-c.png';
import whiteTrogLogo from '@/assets/trog/img/logo-h-w.png';


const drawerWidth = 30;

const StyledAppBar = styled(AppBar)(({ theme}) => ({
    zIndex: theme.zIndex.drawer + 2,
    backgroundColor: theme.palette.mode === 'dark' ?
        '#121212' :
        'white',
    backgroundImage: theme.palette.mode === 'dark' ?
        'linear-gradient(rgba(255, 255, 255, 0.09), rgba(255, 255, 255, 0.09))' :
        undefined
}));

const StyledToolbar = styled(Toolbar)(({ theme}) => ({
    background: theme.palette.mode === 'dark' ?
        undefined :
        theme.palette.common.white,
    color: theme.palette.mode === 'dark' ?
        undefined :
        theme.palette.primary.main,
    minHeight: `${theme.spacing(8)} !important`
}))

const StyledTitle = styled(Typography)(({ theme}) => ({
    color: theme.palette.mode === 'dark' ?
        theme.palette.common.white :
        theme.palette.primary.main,
    textDecoration: 'none',
    textTransform: 'uppercase'
}))

const StyledTitleLink = styled(Link)(({theme}) => ({
    marginRight: 'auto',
    marginLeft: theme.spacing(2),
    textDecoration: 'none'
}))

const StyledListItem = styled(ListItem)(({theme}) => ({
    minHeight: theme.spacing(5)
}))

const StyledListItemButton = styled(ListItemButton)(({theme}) => ({
    padding: theme.spacing(0, 2),
    minHeight: theme.spacing(5),
    borderBottom: `1px solid ${theme.palette.grey[300]}`
}))

const StyledListItemText = styled(ListItemText)(({theme}) => ({
    margin: theme.spacing(0.5, 0)
}))

const StyledListItemIcon = styled(ListItemIcon)(({theme}) => ({
    minWidth: theme.spacing(6),
    fontSize: theme.spacing(3)
}))

const StyledDivider = styled(Divider)(({ theme}) => ({
    margin: `0 !important`
}));


const ToggleFeedbackButton = () => {
    const [feedback, setFeedback] = useState(() => Sentry.getFeedback());
    const chosenTheme = useContext(ChosenThemeContext);

    const buttonRef = useRef<HTMLAnchorElement>()

    useEffect(() => {
        if (buttonRef.current) {
            const unsubscribe = feedback?.attachTo(
                buttonRef.current, 
                { 
                    colorScheme: chosenTheme === 'dark' ? 'dark' : chosenTheme === 'light' ? 'light' : 'system'
                }
            );

            return () => {
                unsubscribe?.()
            }
        }
    }, [buttonRef, chosenTheme])

    return (
        <Tooltip 
            title="Report a Bug"
        >
            <IconButton
                aria-label="theme"
                color="inherit"
                ref={buttonRef as any}
            >
                <FontAwesomeIcon icon={faMegaphone} fixedWidth />
            </IconButton>
        </Tooltip>
    );
}

const Navigation: FC = () => {
    const theme = useTheme();

    const { data: user } = useContext(AuthenticatedUserContext);

    const { data: trial, oid: trialOid } = useContext(TrialContext);

    const { code: institutionCode } = useContext(InstitutionContext);

    const { data: { studyNumber: participantStudyNumber } = { studyNumber: undefined } } = useContext(ParticipantContext);

    const disabledTrial = trialOid && !!trial && (!trial.status || trial.status === ConfigurationStatus.Disabled) ;

    const  links = useMemo(() => {
        if (trialOid && disabledTrial) {
            return [
                {
                    icon: AppSettings.icons.configuration,
                    newTab: false,
                    route: true,
                    title: 'Configuration',
                    url: `/${trialOid}/configuration`,
                }
            ]
        }

        if (participantStudyNumber && institutionCode && trialOid) {
            return [
                {
                    icon: AppSettings.icons.institution,
                    newTab: false,
                    route: true,
                    title: 'Institutions',
                    url: `/${trialOid}/institutions`,
                },
                {
                    icon: AppSettings.icons.participant,
                    newTab: false,
                    route: true,
                    title: 'Participants',
                    url: `/${trialOid}/institutions/${institutionCode}/participants/${participantStudyNumber}`,
                },
                {
                    icon: AppSettings.icons.note,
                    newTab: false,
                    route: true,
                    title: 'Notes & Queries',
                    url: `/${trialOid}/institutions/${institutionCode}/participants/${participantStudyNumber}/notes-and-queries`,
                },
                {
                    icon: AppSettings.icons.finding,
                    newTab: false,
                    route: true,
                    title: 'Findings',
                    url: `/${trialOid}/institutions/${institutionCode}/participants/${participantStudyNumber}/findings`,
                },
                {
                    icon: AppSettings.icons.report,
                    newTab: false,
                    route: true,
                    title: 'Report',
                    url: `/${trialOid}/institutions/${institutionCode}/participants/${participantStudyNumber}/report`,
                },
                {
                    icon: AppSettings.icons.configuration,
                    newTab: false,
                    route: true,
                    title: 'Configuration',
                    url: `/${trialOid}/configuration`,
                }
            ]
        } 

        if (participantStudyNumber && trialOid) {
            return [
                {
                    icon: AppSettings.icons.institution,
                    newTab: false,
                    route: true,
                    title: 'Institutions',
                    url: `/${trialOid}/institutions`,
                },
                {
                    icon: AppSettings.icons.participant,
                    newTab: false,
                    route: true,
                    title: 'Participants',
                    url: `/${trialOid}/participants/${participantStudyNumber}`,
                },
                {
                    icon: AppSettings.icons.note,
                    newTab: false,
                    route: true,
                    title: 'Notes & Queries',
                    url: `/${trialOid}/participants/${participantStudyNumber}/notes-and-queries`,
                },
                {
                    icon: AppSettings.icons.finding,
                    newTab: false,
                    route: true,
                    title: 'Findings',
                    url: `/${trialOid}/participants/${participantStudyNumber}/findings`,
                },
                {
                    icon: AppSettings.icons.report,
                    newTab: false,
                    route: true,
                    title: 'Report',
                    url: `/${trialOid}/participants/${participantStudyNumber}/report`,
                },
                {
                    icon: AppSettings.icons.configuration,
                    newTab: false,
                    route: true,
                    title: 'Configuration',
                    url: `/${trialOid}/configuration`,
                }
            ]
        } 

        if (institutionCode && trialOid) {
            return [
                {
                    icon: AppSettings.icons.institution,
                    newTab: false,
                    route: true,
                    title: 'Institutions',
                    url: `/${trialOid}/institutions`,
                },
                {
                    icon: AppSettings.icons.participant,
                    newTab: false,
                    route: true,
                    title: 'Participants',
                    url: `/${trialOid}/institutions/${institutionCode}/participants`,
                },
                {
                    icon: AppSettings.icons.note,
                    newTab: false,
                    route: true,
                    title: 'Notes & Queries',
                    url: `/${trialOid}/institutions/${institutionCode}/notes-and-queries`,
                },
                {
                    icon: AppSettings.icons.finding,
                    newTab: false,
                    route: true,
                    title: 'Findings',
                    url: `/${trialOid}/institutions/${institutionCode}/findings`,
                },
                {
                    icon: AppSettings.icons.report,
                    newTab: false,
                    route: true,
                    title: 'Report',
                    url: `/${trialOid}/institutions/${institutionCode}/report`,
                },
                {
                    icon: AppSettings.icons.configuration,
                    newTab: false,
                    route: true,
                    title: 'Configuration',
                    url: `/${trialOid}/configuration`,
                }
            ]
        } 

        if (trialOid) {
            return  [
                {
                    icon: AppSettings.icons.institution,
                    newTab: false,
                    route: true,
                    title: 'Institutions',
                    url: `/${trialOid}/institutions`,
                },
                {
                    icon: AppSettings.icons.participant,
                    newTab: false,
                    route: true,
                    title: 'Participants',
                    url: `/${trialOid}/participants`,
                },
                {
                    icon: AppSettings.icons.note,
                    newTab: false,
                    route: true,
                    title: 'Notes & Queries',
                    url: `/${trialOid}/notes-and-queries`,
                },
                {
                    icon: AppSettings.icons.finding,
                    newTab: false,
                    route: true,
                    title: 'Findings',
                    url: `/${trialOid}/findings`,
                },
                {
                    icon: AppSettings.icons.report,
                    newTab: false,
                    route: true,
                    title: 'Report',
                    url: `/${trialOid}/report`,
                },
                {
                    icon: AppSettings.icons.configuration,
                    newTab: false,
                    route: true,
                    title: 'Configuration',
                    url: `/${trialOid}/configuration`,
                }
            ]
        }

        return undefined;
    }, [trialOid, institutionCode, participantStudyNumber, disabledTrial]);

    const showDrawer = !!links && links.length > 0;

    const location = useLocation();

    const chosenTheme = useContext(ChosenThemeContext);
    const setChosenTheme = useContext(ChosenThemeActionContext);

    const changeTheme = useCallback(() => {
        setChosenTheme(chosenTheme === 'dark' ? 'light' : 'dark');
    }, [setChosenTheme, chosenTheme]);

    const [drawOpen, setDrawOpen] = useState(false);

    const handleDrawerOpen = () => {
        setDrawOpen(true);
    };

    const handleDrawerClose = () => {
        setDrawOpen(false);
    };

    const activeLinkIndex = useMemo(() => {
        let i: number | undefined = undefined;
        let length: number = 0;

        if (!links || links.length === 0) {
            return i;
        }


        for (let ci = 0; ci < links.length; ci++) {
            const link = links[ci];

            if (location.pathname.startsWith(link.url) && link.url.length > length) {
                length = link.url.length;
                i = ci;
            }
        }

        return i;
    }, [links, location])

    const drawEnabled = showDrawer;
    
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const handleMenu = (event: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };


    const menuItems = useMemo(() => {
        return links?.map((link, i) => {
            const active = i === activeLinkIndex;

            let linkComponent = null;
            if (link.newTab || !link.route) {
                linkComponent = (
                    <StyledListItem
                        key={i}
                        disablePadding
                    >
                        <StyledListItemButton
                            //@ts-ignore-next-line
                            component="a" 
                            href={link.url}
                        >
                            <StyledListItemIcon>
                                <FontAwesomeIcon fixedWidth icon={link.icon} />
                            </StyledListItemIcon>
                            <StyledListItemText 
                                primary={link.title} 
                                sx={{
                                    display: !drawOpen ? 'none' : undefined
                                }} 
                            />
                        </StyledListItemButton>
                    </StyledListItem>
                );
            }
            else {
                linkComponent = (
                    <StyledListItem
                        key={i}
                        disablePadding
                    >
                        <StyledListItemButton
                            //@ts-ignore-next-line
                            component={RouteLink}
                            to={link.url}
                            sx={{
                                background: active ? theme.palette.mode === 'dark' ? darken(theme.palette.common.white, 0.75) : lighten(theme.palette.common.black, 0.92) : undefined
                            }} 
                        >
                            <StyledListItemIcon>
                                <FontAwesomeIcon fixedWidth icon={link.icon} />
                            </StyledListItemIcon>
                            <StyledListItemText 
                                primary={link.title} 
                                sx={{
                                    display: !drawOpen ? 'none' : undefined
                                }} 
                            />
                        </StyledListItemButton>
                    </StyledListItem>
                );
            }


            if (drawOpen) {
                return linkComponent;
            }

            return (
                <Tooltip title={link.title} placement="right" key={i}>
                    <Box>
                        {linkComponent}
                    </Box>
                </Tooltip>
            );
        })
    }, [links, theme, drawOpen, activeLinkIndex])
    
    let returnToTop = (
        <StyledListItem
            disablePadding
        >
            <StyledListItemButton
                sx={{
                    borderTop: `1px solid ${theme.palette.grey[300]}`,
                }}
                onClick={() => { 
                    window.scrollTo({ left: 0, top: 0, behavior: 'smooth'});
                }}
            >
                <StyledListItemIcon>
                    <FontAwesomeIcon fixedWidth icon={faArrowAltToTop} />
                </StyledListItemIcon>
                <StyledListItemText 
                    primary="Return to Top" 
                    sx={{
                        display: !drawOpen ? 'none' : undefined
                    }} 
                />
            </StyledListItemButton>
        </StyledListItem>
    )
    
    returnToTop = (
        <ShowOnScroll sx={{mt: 'auto'}}>
            <Box>
                {
                    drawOpen ?
                        returnToTop :
                        <Tooltip title="Return to Top" placement="right">
                            <Box>
                                {returnToTop}
                            </Box>
                        </Tooltip>
                }
            </Box>
        </ShowOnScroll>
    );

    let returnToBottom = (
        <StyledListItem
            disablePadding
            sx={{
                borderTop: `1px solid ${theme.palette.grey[300]}`,
            }}
        >
            <StyledListItemButton
                onClick={() => { 
                    window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth'});
                }}
            >
                <StyledListItemIcon>
                    <FontAwesomeIcon fixedWidth icon={faArrowAltToBottom} />
                </StyledListItemIcon>
                <StyledListItemText 
                primary="Go to Bottom" 
                    sx={{
                        display: !drawOpen ? 'none' : undefined
                    }} 
                />
            </StyledListItemButton>
        </StyledListItem>
    );

    
    returnToBottom = (
        <ShowOnScroll
            getTrigger={bottomTrigger}
        >
            <Box>
                {
                    drawOpen ?
                        returnToBottom :
                        <Tooltip title="Go to Bottom" placement="right">
                            <Box>
                                {returnToBottom}
                            </Box>
                        </Tooltip>
                }
            </Box>
        </ShowOnScroll>
    );

    const [openSettings, setOpenSettings ] = useState(false);

    const onSettingsClose = useCallback(() => {
        setOpenSettings(false)
    }, [setOpenSettings])

    return (
        <>
            {
                disabledTrial && !matchPath('/:trialOid/configuration/*', location.pathname) && (
                    <Navigate to={`/${trialOid}/configuration`} replace />
                )
            }
            <StyledAppBar
                position="fixed"
            >
                <StyledToolbar>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerOpen}
                        edge="start"
                        sx={{
                            marginRight: theme.spacing(2),
                            //display: drawOpen ? 'none' : undefined,
                            //visibility: drawEnabled ? undefined : 'hidden'
                            display: drawOpen || !drawEnabled ? 'none' : undefined 
                        }}
                    >
                        <FontAwesomeIcon fixedWidth icon={faBars} />
                    </IconButton>
                    <IconButton
                        color="inherit"
                        aria-label="open drawer"
                        onClick={handleDrawerClose}
                        edge="start"
                        sx={{
                            marginRight: theme.spacing(2),
                            display: !drawOpen ? 'none' : undefined
                        }}
                    >
                        <FontAwesomeIcon fixedWidth icon={faTimes} />
                    </IconButton>
                    <Box
                        sx={{
                            alignSelf: 'center',
                            lineHeight: 0
                        }}
                    >
                        <a href={AppSettings.homeUrl}>
                            <Image
                                sx={{
                                    height: theme.spacing(8)
                                }}
                                alt="Breast Cancer Trials" 
                                src={ 
                                    AppSettings.customer === Customer.Bct ?
                                        chosenTheme === 'dark' ? whiteBctLogo : colorBctLogo :
                                    AppSettings.customer === Customer.Trog ?
                                        chosenTheme === 'dark' ? whiteTrogLogo : colorTrogLogo :
                                    undefined
                                }
                            />
                        </a>
                    </Box>
                    <StyledTitleLink
                        //@ts-ignore-next-line
                        component={RouteLink}
                        to='/'
                    >
                        <StyledTitle 
                            variant="h6"
                        >
                            <strong>Source Data Verification</strong>
                        </StyledTitle>
                    </StyledTitleLink>
                    <>
                        <NavigationParticipantSelector />
                        <NavigationInstitutionSelector />
                        <NavigationTrialSelector />
                    </>
                    {
                        !AppSettings.overrideTheme && (
                            <Tooltip 
                                title={chosenTheme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                            >
                                <IconButton
                                    aria-label="theme"
                                    color="inherit"
                                    onClick={changeTheme}
                                >
                                    <FontAwesomeIcon icon={chosenTheme === 'dark' ? faMoon : faSun } fixedWidth />
                                </IconButton>
                            </Tooltip>
                        )
                    }
                    {
                        AppSettings.sentry.dsn && AppSettings.sentry.enableReportABug && (
                            <ToggleFeedbackButton />
                        )
                    }
                    {
                        !!user && (
                            <>
                                <IconButton
                                    id="btn-auth"
                                    aria-label="account of current user"
                                    aria-controls="menu-auth"
                                    aria-haspopup="true"
                                    onClick={handleMenu}
                                    color="inherit">
                                    <FontAwesomeIcon icon={faUserCircle} />
                                </IconButton>
                                <Menu
                                    id="menu-auth"
                                    anchorEl={anchorEl}
                                    keepMounted
                                    MenuListProps={{
                                        disablePadding: true
                                    }}
                                    transformOrigin={{
                                        vertical: 'top',
                                        horizontal: 'right',
                                    }}
                                    anchorOrigin={{
                                        vertical: 'bottom',
                                        horizontal: 'right',
                                    }}
                                    open={anchorEl?.id === 'btn-auth'}
                                    onClose={handleClose}
                                >
                                    <MenuItem>{user?.displayName ?? user?.userName}</MenuItem>
                                    <StyledDivider />
                                    <MenuItem
                                        onClick={() => {
                                            setOpenSettings(true)
                                        }}
                                    >
                                        Settings
                                    </MenuItem>
                                    <StyledDivider />
                                    <MenuItem component="a" href="/auth/logout">
                                        Logout
                                    </MenuItem>
                                </Menu>
                            </>
                        )
                    }
                </StyledToolbar>
            </StyledAppBar>
            <SettingsDialog
                open={openSettings}
                onClose={onSettingsClose}
            />
            {
                drawEnabled && (
                    <Drawer
                        variant="permanent"
                        sx={{
                            width: drawOpen ? theme.spacing(drawerWidth) : 0,
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                            transition: drawOpen ? 
                                theme.transitions.create('width', {
                                    easing: theme.transitions.easing.sharp,
                                    duration: theme.transitions.duration.enteringScreen,
                                }) :
                                theme.transitions.create('width', {
                                    easing: theme.transitions.easing.sharp,
                                    duration: theme.transitions.duration.leavingScreen,
                                }),
                            overflowX: drawOpen ? 'hidden' : 'hidden',
                            [theme.breakpoints.up('sm')]: drawOpen ? 
                                undefined : 
                                {
                                    width: theme.spacing(8),
                                },
                            '& .MuiPaper-root': {
                                width: drawOpen ? theme.spacing(drawerWidth) : 0,
                                transition: drawOpen ? 
                                    theme.transitions.create('width', {
                                        easing: theme.transitions.easing.sharp,
                                        duration: theme.transitions.duration.enteringScreen,
                                    }) :
                                    theme.transitions.create('width', {
                                        easing: theme.transitions.easing.sharp,
                                        duration: theme.transitions.duration.leavingScreen,
                                    }),
                                overflowX: drawOpen ? 'hidden' : 'hidden',
                                [theme.breakpoints.up('sm')]: drawOpen ? 
                                    undefined : 
                                    {
                                        width: theme.spacing(8),
                                    },
                            }
                        }}
                        // classes={{
                        //     paper: clsx({
                        //         [classes.drawerOpen]: drawOpen,
                        //         [classes.drawerClose]: !drawOpen,
                        //     }),
                        // }}
                    >
                        <Box 
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                padding: theme.spacing(0, 1),
                                // necessary for content to be below app bar
                                ...(theme.mixins.toolbar as any),
                                minHeight: `${theme.spacing(8)} !important`
                            }}
                        />
                        <List disablePadding sx={{ display: 'flex', flexDirection: 'column', height: '100%'}}>
                            {
                                menuItems
                            }
                            {returnToTop}
                            {returnToBottom}
                        </List>
                    </Drawer>
                )
            }
        </>
    );
}

export default Navigation;